import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { TYPE_FAMILIES, slug } from "@/lib/questionTypes";
import { KineticText, Reveal } from "@/components/motion/primitives";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const countLabel = (n) => (n == null ? null : `${n} question${n === 1 ? "" : "s"}`);

function TypeCard({ t, count }) {
  const label = countLabel(count);
  return (
    <Link
      to={`/type/${encodeURIComponent(t.name)}`}
      data-testid={`types-card-${slug(t.name)}`}
      className="group flex h-full flex-col gap-4 rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-gold"
    >
      <h3 className="font-serif text-xl font-bold text-foreground">{t.name}</h3>
      <p className="text-sm leading-relaxed text-foreground/80">{t.definition}</p>
      <p className="text-sm italic leading-relaxed text-muted-foreground">{t.invitation}</p>
      <span className="mt-auto flex items-center gap-1.5 pt-2 font-mono text-xs text-muted-foreground" data-testid={`types-card-footer-${slug(t.name)}`}>
        {label && <span>{label} ·</span>}
        <span className="text-gold">Explore {t.name}</span>
        <ArrowRight className="h-3.5 w-3.5 text-gold transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

function Family({ f, counts }) {
  return (
    <section id={f.id} className="scroll-mt-24" data-testid={`types-family-${f.id}`}>
      <Reveal>
        <h2 className="font-serif text-2xl font-bold text-foreground sm:text-3xl">{f.name}</h2>
        <p className="mt-2 max-w-2xl text-base text-muted-foreground md:text-lg">{f.blurb}</p>
      </Reveal>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {f.types.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.05} className="h-full">
            <TypeCard t={t} count={counts?.[t.name]} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function ExploreTypes() {
  const { data } = useQuery({ queryKey: ["types"], queryFn: async () => (await api.get("/types")).data });
  const counts = data ? Object.fromEntries(data.types.filter((t) => t.count > 0).map((t) => [t.type, t.count])) : null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="explore-types">
      <Seo title="Explore by Type of Question" description="Some questions grieve. Some confront. Some teach. Discover what each type of question is doing in Scripture." />
      <header className="mb-10">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold" data-testid="types-eyebrow">Every question is an invitation.</span>
        <KineticText as="h1" text="Explore by type of question" className="mt-2 block font-serif text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl" testId="types-headline" />
        <Reveal delay={0.3}>
          <p className="mt-3 max-w-2xl text-muted-foreground" data-testid="types-intro">Some questions grieve. Some confront. Some teach. Discover what each type is doing in Scripture and what it invites you to consider.</p>
          <p className="mt-6 text-sm font-semibold text-foreground">Choose a family to begin, or browse them all.</p>
          <nav className="mt-3 flex flex-wrap gap-2" aria-label="Question families" data-testid="types-jump-links">
            {TYPE_FAMILIES.map((f) => (
              <a key={f.id} href={`#${f.id}`} data-testid={`types-jump-${f.id}`}
                className="rounded-full border border-border px-3.5 py-1.5 text-sm text-muted-foreground transition-colors duration-200 hover:border-gold hover:text-foreground">
                {f.name}
              </a>
            ))}
          </nav>
        </Reveal>
      </header>

      <div className="space-y-16">
        {TYPE_FAMILIES.map((f) => <Family key={f.id} f={f} counts={counts} />)}
      </div>

      <Reveal>
        <aside className="mt-20 rounded-2xl border border-border bg-muted/40 p-6 sm:p-8" data-testid="types-context-note">
          <h2 className="font-serif text-xl font-bold text-foreground">Read the exchange. Then reflect.</h2>
          <div className="mt-3 max-w-3xl space-y-3 text-sm leading-relaxed text-muted-foreground sm:text-base" data-testid="types-context-body">
            <p>Start with a question that catches your attention. Notice who asked, who was addressed, and what was happening. Then consider what the exchange invites you to notice today.</p>
            <p>Some questions seek understanding; others challenge, accuse, or deceive. You don&rsquo;t have to agree with the speaker to learn from the exchange. And because a question can do more than one thing, you may find it under more than one type.</p>
          </div>
        </aside>
      </Reveal>

      <Reveal>
        <section className="mt-16 border-t border-border pt-12" data-testid="types-closing">
          <h2 className="font-serif text-3xl font-bold text-foreground">Choose a question. Stay with it.</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">You don&apos;t have to begin with an answer. Start with a question that makes you pause, and read the story around it.</p>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <Link to="/explore" data-testid="types-closing-explore" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5">
              Explore all questions <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/daily" data-testid="types-closing-daily" className="inline-flex items-center gap-2 text-sm font-semibold text-gold hover:underline">
              Read today&apos;s question <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
